import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { api } from '@/lib/api'

interface Proposal {
  id: string
  lead_name: string
  lead_email: string
  status: string
  created_at: string
  updated_at: string
}

export const Proposals: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState('')
  const [generateError, setGenerateError] = useState('')
  const limit = 20

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['proposals', page, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams()
      params.append('page', String(page))
      params.append('limit', String(limit))
      if (statusFilter) params.append('status', statusFilter)
      const response = await api.get(`/proposals?${params.toString()}`)
      return response.data
    },
  })

  // Auto-generate a proposal when arriving from LeadDetail (?leadId=...)
  const leadIdToGenerate = searchParams.get('leadId')
  const clearLeadIdParam = () => {
    searchParams.delete('leadId')
    setSearchParams(searchParams, { replace: true })
  }

  const generateMutation = useMutation({
    mutationFn: async (leadId: string) => {
      const response = await api.post('/proposals/generate', { leadId })
      return response.data.data
    },
    onSuccess: (proposal) => {
      clearLeadIdParam()
      if (proposal?.id) {
        navigate(`/proposals/${proposal.id}`)
      } else {
        refetch()
      }
    },
    onError: (error: any) => {
      clearLeadIdParam()
      setGenerateError(error.response?.data?.error || t('proposals.generateError'))
      setTimeout(() => setGenerateError(''), 5000)
    },
  })

  const generateTriggered = useRef(false)
  useEffect(() => {
    if (leadIdToGenerate && !generateTriggered.current) {
      generateTriggered.current = true
      generateMutation.mutate(leadIdToGenerate)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadIdToGenerate])

  const proposals: Proposal[] = data?.data || []
  const meta = data?.meta || { total: 0, totalPages: 1 }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'bg-gray-500/20 text-gray-400'
      case 'used': return 'bg-green-500/20 text-green-400'
      case 'archived': return 'bg-yellow-500/20 text-yellow-400'
      default: return 'bg-gray-500/20 text-gray-400'
    }
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
        <h1 className="text-3xl font-bold">{t('proposals.title')}</h1>
        <button
          onClick={() => navigate('/leads')}
          className="px-4 py-2 bg-[#006cd4] text-white rounded-lg hover:bg-[#0052a3] transition-colors"
        >
          {t('proposals.newProposal')}
        </button>
      </div>

      {/* Filters */}
      {generateMutation.isPending && (
        <div className="mb-4 p-4 rounded-lg bg-[#006cd4]/10 border border-[#006cd4]/20 text-[#006cd4]">
          {t('proposals.generating')}
        </div>
      )}
      {generateError && (
        <div className="mb-4 p-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400">
          {generateError}
        </div>
      )}
      <div className="bg-[#12121a] border border-white/10 rounded-xl p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}
            className="px-4 py-2 bg-[#1a1a25] border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#006cd4]"
          >
            <option value="">{t('proposals.allStatus')}</option>
            <option value="draft">{t('proposals.draft')}</option>
            <option value="used">{t('proposals.used')}</option>
            <option value="archived">{t('proposals.archived')}</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-[#12121a] border border-white/10 rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-400">{t('proposals.loading')}</div>
        ) : proposals.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <p className="text-lg mb-2">{t('proposals.noProposalsTitle')}</p>
            <p className="text-sm">{t('proposals.noProposalsDesc')}</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">{t('proposals.lead')}</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">{t('common.status')}</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">{t('proposals.created')}</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">{t('proposals.updated')}</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {proposals.map((proposal) => (
                    <tr
                      key={proposal.id}
                      className="border-b border-white/5 hover:bg-white/5 cursor-pointer"
                      onClick={() => navigate(`/proposals/${proposal.id}`)}
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium">{proposal.lead_name}</div>
                        <div className="text-sm text-gray-500">{proposal.lead_email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(proposal.status)}`}>
                          {t(`proposals.${proposal.status}`)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-sm">
                        {new Date(proposal.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-sm">
                        {new Date(proposal.updated_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            navigate(`/proposals/${proposal.id}`)
                          }}
                          className="px-3 py-1 text-sm bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                        >
                          {t('common.edit')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-white/10">
              <div className="text-sm text-gray-400">
                {t('common.showingOf', { count: proposals.length, total: meta.total })}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 bg-white/5 hover:bg-white/10 rounded-lg disabled:opacity-30 transition-colors"
                >
                  {t('common.previous')}
                </button>
                <span className="px-3 py-1 text-sm">
                  {t('common.pageOf', { page, total: meta.totalPages || 1 })}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(meta.totalPages || 1, p + 1))}
                  disabled={page >= meta.totalPages}
                  className="px-3 py-1 bg-white/5 hover:bg-white/10 rounded-lg disabled:opacity-30 transition-colors"
                >
                  {t('common.next')}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
